import { useEffect, useMemo, useState } from "react";
import Header from "../components/common/header";
import Footer from "../components/common/footer";
import {
  Activity,
  Bot,
  CircleDollarSign,
  Copy,
  Flame,
  Gauge,
  GitBranch,
  Plus,
  Radar,
  Search,
  Trash2,
  Wallet,
} from "lucide-react";

const WS_URL = "wss://cryptospace.network/wallet-feed";
const TRACKED_WALLETS_KEY = "chainintel-tracked-wallets";

const EVENT_WEIGHTS = {
  LARGE_VALUE_TX: 90,
  REAL_SWAP: 70,
  BOT_LIKE_TX: 80,
  COMPLEX_TX: 75,
  GAS_SPIKE: 55,
  CONTRACT_CALL: 45,
  NEW_CONTRACT_DEPLOY: 85,
  FIRST_TOKEN_ACTIVITY: 85,
  VALUE_TX: 40,
};

function shortAddress(address) {
  if (!address) return "Unknown";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function normalizeAddress(address) {
  return address.trim().toLowerCase();
}

function isLikelyAddress(address) {
  return /^0x[a-fA-F0-9]{6,}$/.test(address.trim());
}

function getWallet(signal) {
  const payload = signal.payload || {};
  return payload.from || payload.to || signal.from || signal.to || null;
}

function getSignalAddresses(signal) {
  const payload = signal.payload || {};
  return [
    payload.from,
    payload.to,
    payload.contract,
    payload.token,
    signal.from,
    signal.to,
    signal.contract,
  ].filter(Boolean);
}

function getAmount(payload) {
  return Number(payload.amountIn || payload.amount || payload.value || 0) || 0;
}

function formatCompact(number) {
  if (!number) return "0";
  if (number >= 1e12) return `${(number / 1e12).toFixed(1)}T`;
  if (number >= 1e9) return `${(number / 1e9).toFixed(1)}B`;
  if (number >= 1e6) return `${(number / 1e6).toFixed(1)}M`;
  if (number >= 1e3) return `${(number / 1e3).toFixed(1)}K`;
  return number.toFixed(2);
}

function getWalletProfile(wallet) {
  if (wallet.events.BOT_LIKE_TX >= 3) {
    return {
      label: "Automation Pattern",
      icon: <Bot size={18} />,
      tone: "purple",
      insight: "Repeated bot-like execution. Useful for detecting systematic market behavior.",
    };
  }

  if (wallet.events.LARGE_VALUE_TX || wallet.volume > 1e7) {
    return {
      label: "Whale Movement",
      icon: <CircleDollarSign size={18} />,
      tone: "blue",
      insight: "Large value movement. Watch for liquidity shifts or pre-positioning.",
    };
  }

  if (wallet.events.FIRST_TOKEN_ACTIVITY || wallet.events.NEW_CONTRACT_DEPLOY) {
    return {
      label: "Early Discovery",
      icon: <Radar size={18} />,
      tone: "green",
      insight: "Early lifecycle activity. Good candidate for token or contract discovery.",
    };
  }

  if (wallet.events.GAS_SPIKE || wallet.events.COMPLEX_TX) {
    return {
      label: "Execution Pressure",
      icon: <Flame size={18} />,
      tone: "orange",
      insight: "Complex or priority execution behavior. May indicate competitive routing.",
    };
  }

  return {
    label: "Active Wallet",
    icon: <Wallet size={18} />,
    tone: "neutral",
    insight: "Repeated live activity. Keep it visible until the pattern becomes clearer.",
  };
}

function getPrimaryToken(wallet) {
  const topPair = Object.entries(wallet.tokens).sort((a, b) => b[1] - a[1])[0];
  if (topPair?.[0] && topPair[0] !== "?->?") return topPair[0];

  const topEvent = Object.entries(wallet.events).sort((a, b) => b[1] - a[1])[0]?.[0];

  const activityLabels = {
    COMPLEX_TX: "Complex execution",
    GAS_SPIKE: "Gas pressure",
    BOT_LIKE_TX: "Bot pattern",
    CONTRACT_CALL: "Contract calls",
    LARGE_VALUE_TX: "Large transfer",
    VALUE_TX: "Value transfer",
    FIRST_TOKEN_ACTIVITY: "New token activity",
    NEW_CONTRACT_DEPLOY: "New contract",
    REAL_SWAP: "Swap activity",
  };

  return activityLabels[topEvent] || "Live wallet activity";
}

export default function SmartMoneyPage() {
  const [wallets, setWallets] = useState({});
  const [trackedWallets, setTrackedWallets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(TRACKED_WALLETS_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [trackedEvents, setTrackedEvents] = useState({});
  const [walletInput, setWalletInput] = useState("");
  const [inputError, setInputError] = useState("");
  const [status, setStatus] = useState("connecting");

  useEffect(() => {
    const ws = new WebSocket(WS_URL);

    ws.onopen = () => setStatus("live");
    ws.onclose = () => setStatus("offline");
    ws.onerror = () => setStatus("degraded");

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const payload = msg.payload || {};
        const wallet = getWallet(msg);
        const addresses = getSignalAddresses(msg).map(normalizeAddress);

        if (!msg.event || !wallet) return;

        const amount = getAmount(payload);
        const pair = `${payload.tokenInSymbol || "?"}->${payload.tokenOutSymbol || "?"}`;
        const eventWeight = EVENT_WEIGHTS[msg.event] || 25;

        setWallets((prev) => {
          const current = prev[wallet] || {
            address: wallet,
            volume: 0,
            trades: 0,
            score: 0,
            tokens: {},
            events: {},
            recent: [],
            firstSeen: Date.now(),
            lastSeen: Date.now(),
          };

          const valueScore = amount > 0 ? Math.min(120, Math.log10(amount + 1) * 12) : 0;
          const nextEvents = {
            ...current.events,
            [msg.event]: (current.events[msg.event] || 0) + 1,
          };
          const nextTokens = {
            ...current.tokens,
            [pair]: (current.tokens[pair] || 0) + 1,
          };

          return {
            ...prev,
            [wallet]: {
              ...current,
              volume: current.volume + amount,
              trades: current.trades + 1,
              score: Math.round(current.score + eventWeight + valueScore),
              events: nextEvents,
              tokens: nextTokens,
              lastSeen: Date.now(),
              recent: [
                {
                  event: msg.event,
                  pair,
                  amount,
                  at: Date.now(),
                },
                ...current.recent,
              ].slice(0, 4),
            },
          };
        });

        setTrackedEvents((prev) => {
          const matched = trackedWallets.filter((trackedWallet) =>
            addresses.includes(normalizeAddress(trackedWallet.address))
          );

          if (!matched.length) return prev;

          const next = { ...prev };
          matched.forEach((trackedWallet) => {
            const key = normalizeAddress(trackedWallet.address);
            next[key] = [
              {
                event: msg.event,
                amount,
                pair,
                wallet,
                at: Date.now(),
              },
              ...(next[key] || []),
            ].slice(0, 20);
          });

          return next;
        });
      } catch {
        // Ignore malformed messages.
      }
    };

    return () => ws.close();
  }, [trackedWallets]);

  useEffect(() => {
    localStorage.setItem(TRACKED_WALLETS_KEY, JSON.stringify(trackedWallets));
  }, [trackedWallets]);

  const addTrackedWallet = (event) => {
    event.preventDefault();
    const address = walletInput.trim();

    if (!isLikelyAddress(address)) {
      setInputError("Paste a wallet address that starts with 0x.");
      return;
    }

    const normalized = normalizeAddress(address);
    if (trackedWallets.some((wallet) => normalizeAddress(wallet.address) === normalized)) {
      setInputError("This wallet is already being tracked.");
      return;
    }

    setTrackedWallets((prev) => [
      {
        address,
        addedAt: Date.now(),
      },
      ...prev,
    ]);
    setWalletInput("");
    setInputError("");
  };

  const removeTrackedWallet = (address) => {
    const normalized = normalizeAddress(address);
    setTrackedWallets((prev) =>
      prev.filter((wallet) => normalizeAddress(wallet.address) !== normalized)
    );
    setTrackedEvents((prev) => {
      const next = { ...prev };
      delete next[normalized];
      return next;
    });
  };

  const copyWallet = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
    } catch {
      // Clipboard is a convenience action; no fallback needed.
    }
  };

  const rankedWallets = useMemo(() => {
    return Object.values(wallets)
      .map((wallet) => ({
        ...wallet,
        profile: getWalletProfile(wallet),
        primaryRoute: getPrimaryToken(wallet),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 24);
  }, [wallets]);

  const summary = useMemo(() => {
    const activeWallets = Object.keys(wallets).length;
    const highIntent = rankedWallets.filter((wallet) => wallet.score >= 200).length;
    const botPatterns = rankedWallets.filter((wallet) => wallet.events.BOT_LIKE_TX).length;
    const whaleMoves = rankedWallets.filter(
      (wallet) => wallet.events.LARGE_VALUE_TX || wallet.volume > 1e7
    ).length;

    return { activeWallets, highIntent, botPatterns, whaleMoves };
  }, [rankedWallets, wallets]);

  return (
    <div className="wallet-intel-page">
      <Header />

      <main className="wallet-intel-shell">
        <section className="wallet-intel-hero">
          <div>
            <div className="overview-kicker">
              <Radar size={16} />
              Wallet intelligence
            </div>
            <h1>Find wallets worth watching.</h1>
            <p>
              This tool ranks live wallets by intent, not noise. Use it to spot
              whales, automation patterns, early token discovery, and execution
              pressure as they appear in the stream.
            </p>
          </div>
          <div className={`overview-status ${status}`}>
            <span />
            {status === "live" ? "Live wallet feed" : `Feed ${status}`}
          </div>
        </section>

        <section className="wallet-intel-stats">
          <StatCard icon={<Wallet />} label="Wallets observed" value={summary.activeWallets} />
          <StatCard icon={<Gauge />} label="High-intent wallets" value={summary.highIntent} />
          <StatCard icon={<Bot />} label="Automation patterns" value={summary.botPatterns} />
          <StatCard icon={<CircleDollarSign />} label="Whale candidates" value={summary.whaleMoves} />
        </section>

        <section className="wallet-tracker">
          <div className="wallet-tracker-copy">
            <div className="overview-kicker">
              <Search size={16} />
              Personal tracking
            </div>
            <h2>Track a wallet you care about.</h2>
            <p>
              Paste any wallet address and ChainIntel will watch the live stream
              for matches. Use this for your own wallet, a suspicious address,
              or a smart-money wallet you want to monitor.
            </p>
          </div>

          <form className="wallet-track-form" onSubmit={addTrackedWallet}>
            <input
              value={walletInput}
              onChange={(event) => {
                setWalletInput(event.target.value);
                setInputError("");
              }}
              placeholder="Paste wallet address, e.g. 0x..."
            />
            <button type="submit">
              <Plus size={17} />
              Track
            </button>
            {inputError && <span>{inputError}</span>}
          </form>

          <div className="tracked-wallets">
            {trackedWallets.length ? (
              trackedWallets.map((trackedWallet) => {
                const key = normalizeAddress(trackedWallet.address);
                const events = trackedEvents[key] || [];
                const lastEvent = events[0];

                return (
                  <article className="tracked-wallet-card" key={key}>
                    <div className="tracked-wallet-head">
                      <div>
                        <strong>{shortAddress(trackedWallet.address)}</strong>
                        <span>
                          {lastEvent
                            ? `${lastEvent.event} matched ${Math.floor((Date.now() - lastEvent.at) / 1000)}s ago`
                            : "Waiting for the next live match"}
                        </span>
                      </div>
                      <div className="tracked-wallet-actions">
                        <button type="button" onClick={() => copyWallet(trackedWallet.address)}>
                          <Copy size={15} />
                        </button>
                        <button type="button" onClick={() => removeTrackedWallet(trackedWallet.address)}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    <div className="tracked-wallet-summary">
                      <div>
                        <span>Matches</span>
                        <strong>{events.length}</strong>
                      </div>
                      <div>
                        <span>Status</span>
                        <strong>{events.length ? "Active" : "Watching"}</strong>
                      </div>
                    </div>

                    <div className="tracked-wallet-events">
                      {events.slice(0, 3).map((trackedEvent) => (
                        <span key={`${trackedEvent.event}-${trackedEvent.at}`}>
                          {trackedEvent.event}
                        </span>
                      ))}
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="tracked-wallet-empty">
                No wallets tracked yet. Add one above and keep this page open to
                watch for live activity.
              </div>
            )}
          </div>
        </section>

        <section className="wallet-intel-tool">
          <div className="wallet-intel-tool-head">
            <div>
              <h2>Watchlist Candidates</h2>
              <p>
                Prioritized wallets with the clearest behavioral signal in the
                current live window.
              </p>
            </div>
            <div className="wallet-intel-legend">
              <span>Score = event weight + value + repetition</span>
            </div>
          </div>

          <div className="wallet-intel-grid">
            {rankedWallets.length ? (
              rankedWallets.map((wallet, index) => (
                <WalletCard
                  wallet={wallet}
                  rank={index + 1}
                  key={wallet.address}
                  onCopy={copyWallet}
                  onTrack={(address) => {
                    const normalized = normalizeAddress(address);
                    if (trackedWallets.some((item) => normalizeAddress(item.address) === normalized)) {
                      return;
                    }
                    setTrackedWallets((prev) => [{ address, addedAt: Date.now() }, ...prev]);
                  }}
                />
              ))
            ) : (
              <div className="wallet-intel-empty">
                Waiting for live wallet activity. When signals arrive, this page
                will turn them into ranked investigation candidates.
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="wallet-intel-stat">
      <div>{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function WalletCard({ wallet, rank, onCopy, onTrack }) {
  const topEvents = Object.entries(wallet.events)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <article className={`wallet-card ${wallet.profile.tone}`}>
      <div className="wallet-card-top">
        <div>
          <span className="wallet-rank">#{rank}</span>
          <h3>{shortAddress(wallet.address)}</h3>
          <code>{wallet.address}</code>
        </div>
        <div className="wallet-score">{wallet.score}</div>
      </div>

      <div className="wallet-profile">
        {wallet.profile.icon}
        <strong>{wallet.profile.label}</strong>
      </div>

      <p className="wallet-insight">{wallet.profile.insight}</p>

      <div className="wallet-metrics">
        <div>
          <span>Volume</span>
          <strong>{formatCompact(wallet.volume)}</strong>
        </div>
        <div>
          <span>Trades</span>
          <strong>{wallet.trades}</strong>
        </div>
        <div>
          <span>Main Activity</span>
          <strong>{wallet.primaryRoute}</strong>
        </div>
      </div>

      <div className="wallet-events">
        {topEvents.map(([event, count]) => (
          <span key={event}>
            {event}: {count}
          </span>
        ))}
      </div>

      <div className="wallet-actions">
        <button type="button">
          <Search size={15} />
          Investigate
        </button>
        <button type="button" onClick={() => onCopy(wallet.address)}>
          <Copy size={15} />
          Copy address
        </button>
        <button type="button" onClick={() => onTrack(wallet.address)}>
          <Plus size={15} />
          Track
        </button>
        <button type="button">
          <GitBranch size={15} />
          Trace
        </button>
        <span>
          <Activity size={14} />
          {Math.floor((Date.now() - wallet.lastSeen) / 1000)}s ago
        </span>
      </div>
    </article>
  );
}
